<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import useAuth from '@/composables/auth/useAuth';
import useAppConfig from '@/composables/app/useAppConfig';

const props = defineProps({
  isDataSet: Boolean
});

const store = useStore();
const route = useRoute();
const { authUser } = useAuth();
const { currentBreakPoint } = useAppConfig();
const isDarkMode = computed(() => store.state.is_dark_mode);
const pageTitle = computed(() => route.meta?.pageTitle || 'Tenant Administration');

const userDisplayName = computed(() => {
  const fullName = [authUser.value?.first_name, authUser.value?.last_name]
    .filter(Boolean)
    .join(' ')
    .trim();
  return fullName || authUser.value?.username || 'Admin';
});

const userEmail = computed(() => authUser.value?.email || '');

const initials = computed(() => {
  return userDisplayName.value
    .split(' ')
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
});

const toggleMode = () => {
  window.$appSetting.toggleMode(isDarkMode.value ? 'light' : 'dark');
};

const handleLogout = () => {
  store.commit('auth/accessToken', null);
  localStorage.removeItem('refresh');
  localStorage.clear();
  authUser.value = null;
  location.reload();
};
</script>

<template>
  <div
    class="super-admin-shell"
    :class="{ 'super-admin-is-dark': isDarkMode }"
  >
    <aside class="super-admin-sidebar">
      <router-link to="/admin/" class="super-admin-brand">
        <img
          v-if="currentBreakPoint === 'xs'"
          src="/favicon.png"
          alt="AOS"
        />
        <img
          v-else
          src="@/assets/images/logo.png"
          alt="AOS"
        />
        <span>Admin</span>
      </router-link>

      <nav class="super-admin-nav">
        <router-link
          :to="{ name: 'super-admin-tenants' }"
          class="super-admin-nav-item"
        >
          <FeatherIcon type="grid" size="17" />
          <span>Tenants</span>
        </router-link>
        <router-link
          :to="{ name: 'super-admin-cloudflare' }"
          class="super-admin-nav-item"
        >
          <FeatherIcon type="cloud" size="17" />
          <span>Cloudflare Configuration</span>
        </router-link>
        <router-link
          :to="{ name: 'super-admin-carrier-social' }"
          class="super-admin-nav-item"
        >
          <FeatherIcon type="share-2" size="17" />
          <span>Carrier Social Config</span>
        </router-link>
      </nav>
    </aside>

    <main class="super-admin-main">
      <header class="super-admin-header">
        <div>
          <p class="super-admin-eyebrow">Superadmin</p>
          <h4>{{ pageTitle }}</h4>
        </div>

        <div class="super-admin-header-actions">
          <button
            class="admin-icon-btn"
            type="button"
            :aria-label="isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'"
            :title="isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'"
            @click="toggleMode"
          >
            <FeatherIcon
              :type="isDarkMode ? 'sun' : 'moon'"
              size="17"
            />
          </button>

          <div class="dropdown admin-profile-dropdown">
            <button
              id="admin-profile-menu"
              class="admin-profile-trigger"
              type="button"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <span class="admin-avatar">{{ initials }}</span>
              <span class="admin-profile-copy">
                <strong>{{ userDisplayName }}</strong>
                <small>{{ userEmail }}</small>
              </span>
              <FeatherIcon type="chevron-down" size="15" />
            </button>
            <ul
              class="dropdown-menu dropdown-menu-end admin-profile-menu"
              aria-labelledby="admin-profile-menu"
            >
              <li>
                <router-link class="dropdown-item" to="/">
                  <FeatherIcon type="corner-up-left" size="16" />
                  <span>Tenant Panel</span>
                </router-link>
              </li>
              <li>
                <router-link class="dropdown-item" :to="{ name: 'user-profile' }">
                  <FeatherIcon type="user" size="16" />
                  <span>Profile</span>
                </router-link>
              </li>
              <li>
                <button class="dropdown-item text-danger" type="button" @click="handleLogout">
                  <FeatherIcon type="log-out" size="16" />
                  <span>Sign Out</span>
                </button>
              </li>
            </ul>
          </div>
        </div>
      </header>

      <section class="super-admin-content">
        <router-view v-if="props.isDataSet" />
        <div v-else class="admin-loader">
          <div class="spinner-border text-primary" role="status"></div>
          <span>Loading admin data...</span>
        </div>
      </section>
    </main>
  </div>
</template>

<style lang="scss" scoped>
.super-admin-shell {
  --admin-page: #f4f7fb;
  --admin-surface: #ffffff;
  --admin-heading: #111827;
  --admin-text: #344054;
  --admin-muted: #667085;
  --admin-border: #e6ebf2;
  --admin-control-border: #d9e1ec;
  --admin-menu-hover: #f2f4f7;
  --admin-shadow: 0 8px 22px rgba(16, 24, 40, 0.06);
  height: 100vh;
  display: flex;
  overflow: hidden;
  background: var(--admin-page);
  color: var(--admin-text);
  color-scheme: light;
}

.super-admin-sidebar {
  width: 268px;
  min-width: 268px;
  height: 100vh;
  flex-shrink: 0;
  overflow-y: auto;
  padding: 18px 14px;
  background: linear-gradient(180deg, #111c34 0%, #0b1223 100%);
  border-right: 1px solid rgba(255, 255, 255, 0.08);
}

.super-admin-brand {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 10px 20px;
  color: #fff;
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;

  img {
    max-width: 136px;
    height: 34px;
    object-fit: contain;
  }
}

.super-admin-nav {
  margin-top: 18px;
}

.super-admin-nav-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 12px;
  color: #aebbd4;
  font-size: 13px;
  font-weight: 700;
  transition: 0.18s ease;

  &:hover,
  &.active,
  &.router-link-active {
    color: #fff;
    background: rgba(67, 97, 238, 0.24);
  }
}

.super-admin-main {
  flex: 1;
  min-width: 0;
  height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.super-admin-header {
  flex-shrink: 0;
  min-height: 76px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 26px;
  background: color-mix(in srgb, var(--admin-surface) 86%, transparent);
  border-bottom: 1px solid var(--admin-border);
  backdrop-filter: blur(14px);

  h4 {
    margin: 0;
    color: var(--admin-heading);
    font-size: 20px;
    font-weight: 800;
  }
}

.super-admin-eyebrow {
  margin: 0 0 3px;
  color: var(--admin-muted);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.super-admin-header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.admin-icon-btn,
.admin-profile-trigger {
  border: 1px solid var(--admin-control-border);
  background: var(--admin-surface);
  color: var(--admin-text);
  box-shadow: var(--admin-shadow);
  transition: border-color 0.16s ease, background-color 0.16s ease,
    color 0.16s ease;
}

.admin-icon-btn {
  width: 40px;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;

  &:hover,
  &:focus-visible {
    border-color: #8093e8;
    background: #f5f7ff;
    color: #3157dc;
  }
}

.admin-profile-trigger {
  min-width: 230px;
  min-height: 44px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 10px;
  border-radius: 14px;
}

.admin-avatar {
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: linear-gradient(135deg, #4361ee, #14b8a6);
  color: #fff;
  font-size: 11px;
  font-weight: 900;
}

.admin-profile-copy {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  strong,
  small {
    max-width: 145px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  strong {
    color: var(--admin-heading);
    font-size: 12px;
  }

  small {
    color: var(--admin-muted);
    font-size: 10px;
  }
}

.admin-profile-menu {
  width: 230px;
  padding: 8px !important;
  border: 1px solid var(--admin-border);
  border-radius: 14px;
  background: var(--admin-surface);
  box-shadow: 0 18px 45px rgba(16, 24, 40, 0.16);

  .dropdown-item {
    display: flex;
    align-items: center;
    gap: 10px;
    min-height: 38px;
    border-radius: 10px;
    font-size: 12px;
    font-weight: 700;
    color: var(--admin-text);

    &:hover,
    &:focus {
      background: var(--admin-menu-hover);
      color: var(--admin-heading);
    }
  }
}

.super-admin-content {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 24px;
  background: var(--admin-page);
}

.admin-loader {
  height: calc(100vh - 124px);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: var(--admin-muted);
  font-weight: 700;
}

:global(.dark) .super-admin-shell {
  background: #070b16;
  color: #d8e2f5;
}

:global(.dark) .super-admin-content {
  background: #070b16;
}

:global(.dark) .super-admin-header,
:global(.dark) .admin-icon-btn,
:global(.dark) .admin-profile-trigger,
:global(.dark) .admin-profile-menu {
  background: #111c34;
  border-color: rgba(148, 163, 184, 0.2);
  color: #d8e2f5;
}

:global(.dark) .super-admin-header h4,
:global(.dark) .admin-profile-copy strong {
  color: #f8fafc;
}

:global(.dark) .super-admin-eyebrow,
:global(.dark) .admin-profile-copy small {
  color: #94a3b8;
}

:global(.dark) .admin-profile-menu .dropdown-item {
  color: #d8e2f5;
}

:global(.dark) .admin-profile-menu .dropdown-item:hover {
  background: #1b2e4b;
}

@media (max-width: 991px) {
  .super-admin-shell {
    flex-direction: column;
  }

  .super-admin-sidebar {
    width: 100%;
    min-width: 0;
    min-height: auto;
  }

  .super-admin-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .admin-profile-trigger {
    min-width: 0;
  }
}
</style>

<style lang="scss">
body.dark,
html.dark {
  background: #070b16 !important;
}

.super-admin-shell.super-admin-is-dark {
  --admin-page: #070b16;
  --admin-surface: #0f172a;
  --admin-heading: #f8fafc;
  --admin-text: #d8e2f5;
  --admin-muted: #94a3b8;
  --admin-border: rgba(148, 163, 184, 0.22);
  --admin-control-border: rgba(148, 163, 184, 0.22);
  --admin-menu-hover: #1e293b;
  --admin-shadow: 0 10px 28px rgba(0, 0, 0, 0.24);
  background: var(--admin-page) !important;
  color: var(--admin-text);
  color-scheme: dark;

  .super-admin-sidebar {
    background: linear-gradient(180deg, #08111f 0%, #050914 100%) !important;
    border-right-color: rgba(148, 163, 184, 0.18) !important;
  }

  .super-admin-main,
  .super-admin-content {
    background: var(--admin-page) !important;
  }

  .super-admin-header,
  .admin-icon-btn,
  .admin-profile-trigger,
  .admin-profile-menu {
    background: var(--admin-surface) !important;
    border-color: var(--admin-border) !important;
    color: var(--admin-text) !important;
  }

  .super-admin-header h4,
  .admin-profile-copy strong {
    color: #f8fafc !important;
  }

  .super-admin-eyebrow,
  .admin-profile-copy small {
    color: #94a3b8 !important;
  }

  .admin-profile-menu .dropdown-item {
    color: #d8e2f5 !important;
  }

  .admin-profile-menu .dropdown-item:hover,
  .admin-profile-menu .dropdown-item:focus {
    background: #1e293b !important;
  }

  .admin-icon-btn:hover,
  .admin-icon-btn:focus-visible {
    background: #1e293b !important;
    border-color: rgba(129, 140, 248, 0.55) !important;
    color: #c7d2fe !important;
  }
}
</style>
